// =============================================================================
// FILE: packages/renderer/src/components/vault/VaultCopyStage.tsx
// PURPOSE: Shown inside QuickIngestDialog after a project scan completes on
//          the desktop app. Copies files from source folder into the R2 Vault
//          with real-time progress and a separate analysis progress bar.
// =============================================================================

import { useCallback, useEffect, useRef, useState } from 'react';
import type { JSX } from 'react';
import {
  FolderOpen, CheckCircle2, AlertTriangle, Database,
  Loader2, ExternalLink, ChevronRight,
} from 'lucide-react';
import { type CopyProgress, type VaultConfig } from '../../stores/vault.store.js';

interface Props {
  sourcePath:    string;
  projectNumber: string;
  vaultConfig:   VaultConfig;
  totalFiles:    number;
  onComplete:    (projDir: string) => void;
  onSkip:        () => void;
}

type CopyState = 'confirm' | 'copying' | 'done' | 'error';

export function VaultCopyStage({ sourcePath, projectNumber, vaultConfig, totalFiles, onComplete, onSkip }: Props): JSX.Element {
  const [copyState, setCopyState]   = useState<CopyState>('confirm');
  const [progress,  setProgress]    = useState<CopyProgress>({ copied: 0, total: totalFiles, currentFile: '', pct: 0 });
  const [result,    setResult]      = useState<{ copied: number; total: number; errors: unknown[]; projDir?: string } | null>(null);
  const [errorMsg,  setErrorMsg]    = useState('');
  const abortRef = useRef(false);

  const startCopy = useCallback(async () => {
    if (!window.r2Vault || !vaultConfig.vaultPath) return;
    abortRef.current = false;
    setCopyState('copying');

    const res = await window.r2Vault.copyToVault(
      { sourcePath, projectNumber, vaultPath: vaultConfig.vaultPath },
      (p) => { if (!abortRef.current) setProgress(p); },
    );

    if (res.success) {
      setResult(res);
      setCopyState('done');
      if (res.projDir) onComplete(res.projDir);
    } else {
      setErrorMsg(String((res as { error?: string }).error ?? 'Copy failed'));
      setCopyState('error');
    }
  }, [sourcePath, projectNumber, vaultConfig, onComplete]);

  const openInExplorer = async () => {
    if (!result?.projDir) return;
    await window.r2Vault?.openInExplorer(result.projDir);
  };

  const destPath = vaultConfig.vaultPath
    ? `${vaultConfig.vaultPath}\\Projects\\${projectNumber}`
    : '';

  return (
    <div className="space-y-4">

      {/* Vault destination info */}
      <div className="flex items-start gap-3 p-3 rounded-sm bg-zinc-900/70 border border-zinc-800">
        <Database className="h-4 w-4 text-teal-400 mt-0.5 shrink-0" />
        <div className="flex-1 min-w-0">
          <div className="text-[11px] uppercase tracking-[0.16em] text-zinc-400 mb-0.5">Vault Destination</div>
          <div className="text-[11.5px] text-zinc-200 font-mono truncate">{destPath}</div>
          <div className="text-[10.5px] text-zinc-500 mt-1">
            Original files stay where they are. R2 creates a structured copy.
          </div>
        </div>
      </div>

      {/* State: confirm */}
      {copyState === 'confirm' && (
        <div className="space-y-3">
          <div className="text-[12px] text-zinc-300 leading-relaxed">
            Copy <strong className="text-zinc-100">{totalFiles.toLocaleString()} files</strong> from source folder into the R2 Vault? Files will be organized into:
            <span className="text-teal-400 ml-1">Source Documents / BIM Models / Drawings / Photos</span>
          </div>
          <div className="flex gap-2">
            <button onClick={onSkip}
              className="flex-1 py-2 text-[11px] uppercase tracking-[0.16em] rounded-sm border border-zinc-700 text-zinc-400 hover:text-zinc-200 transition">
              Skip
            </button>
            <button
              data-testid="vault-copy-start"
              onClick={() => void startCopy()}
              className="flex-1 py-2 text-[11px] uppercase tracking-[0.16em] rounded-sm bg-teal-500/20 border border-teal-500/40 text-teal-300 hover:bg-teal-500/30 transition font-semibold"
            >
              Copy to Vault
            </button>
          </div>
        </div>
      )}

      {/* State: copying */}
      {copyState === 'copying' && (
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <span className="text-[12px] font-semibold text-zinc-100">Copying to Vault</span>
            <Loader2 className="h-4 w-4 text-teal-400 animate-spin" />
          </div>
          <div>
            <div className="flex justify-between text-[11px] text-zinc-400 mb-1.5">
              <span>{progress.copied.toLocaleString()} / {progress.total.toLocaleString()} files</span>
              <span>{progress.pct}%</span>
            </div>
            <div className="w-full h-2 bg-zinc-900 rounded-full overflow-hidden">
              <div className="h-full bg-teal-400 rounded-full transition-all duration-150"
                style={{ width: `${progress.pct}%` }} />
            </div>
          </div>
          <div className="text-[10.5px] text-zinc-500 font-mono truncate">
            {progress.currentFile || '—'}
          </div>
        </div>
      )}

      {/* State: done */}
      {copyState === 'done' && result && (
        <div className="space-y-3">
          <div className="flex items-center gap-2 px-3 py-2.5 rounded-sm"
            style={{ background: 'rgba(37,181,168,0.07)', border: '1px solid rgba(37,181,168,0.25)' }}>
            <CheckCircle2 className="h-4 w-4 text-teal-400 shrink-0" />
            <div className="flex-1">
              <div className="text-[12px] font-semibold text-zinc-100">
                {result.copied.toLocaleString()} files copied to vault
              </div>
              {(result.errors as unknown[]).length > 0 && (
                <div className="text-[10.5px] text-amber-400 mt-0.5">{(result.errors as unknown[]).length} files skipped</div>
              )}
            </div>
          </div>
          <button onClick={() => void openInExplorer()}
            className="w-full flex items-center justify-center gap-2 py-2 text-[11px] rounded-sm border border-zinc-700 text-zinc-300 hover:text-zinc-100 hover:border-zinc-500 transition">
            <ExternalLink className="h-3.5 w-3.5" /> Open Vault in Explorer
          </button>
        </div>
      )}

      {/* State: error */}
      {copyState === 'error' && (
        <div className="flex items-start gap-2 p-3 rounded-sm"
          style={{ background: 'rgba(239,68,68,0.07)', border: '1px solid rgba(239,68,68,0.25)' }}>
          <AlertTriangle className="h-4 w-4 text-rose-400 shrink-0 mt-0.5" />
          <div>
            <div className="text-[12px] font-semibold text-rose-300">Copy failed</div>
            <div className="text-[11px] text-zinc-400 mt-0.5">{errorMsg}</div>
            <button onClick={() => void startCopy()} className="mt-2 text-[11px] text-teal-400 hover:underline">
              Retry
            </button>
          </div>
        </div>
      )}

    </div>
  );
}
